import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Briefcase,
  Calendar,
  Camera,
  Eye,
  Globe,
  IdCard,
  Lock,
  Mail,
  MapPin,
  Upload,
  Pencil,
  Plus,
  Shield,
  Sparkles,
  Trash2,
  User,
  UserCircle2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import Loading from './icon/loading';
import { getApiBaseUrl } from '../config/api';
import { resolveAvatarUrl, handleAvatarError } from '../utils/profilePicUrl';
import { clearAuthToken } from '../utils/authToken';
import './MyProfile.css';

const emptyExperience = () => ({
  job_title: '',
  company: '',
  start_date: '',
  end_date: '',
  is_current: false,
  description: '',
});

const defaultPrivacy = () => ({
  privacy_profile: 'everyone',
  privacy_email: 'connections',
  privacy_country: 'everyone',
  privacy_position: 'everyone',
  privacy_bio: 'everyone',
  privacy_skills: 'everyone',
  privacy_experience: 'everyone',
});

const PRIVACY_LEVELS = [
  { value: 'everyone', label: 'Everyone' },
  { value: 'connections', label: 'Connections' },
  { value: 'only_me', label: 'Only me' },
];

const PROFILE_VISIBILITY = [
  {
    value: 'everyone',
    label: 'Everyone',
    hint: 'Anyone on COOL can view your profile',
    icon: Globe,
  },
  {
    value: 'connections',
    label: 'Connections',
    hint: 'Only people you are connected with',
    icon: Users,
  },
  {
    value: 'only_me',
    label: 'Only me',
    hint: 'Hide your details from everyone else',
    icon: Lock,
  },
];

const INFO_PRIVACY_FIELDS = [
  { key: 'privacy_email', label: 'Email address', hint: 'Your email on your public profile' },
  { key: 'privacy_country', label: 'Country', hint: 'Your location' },
  { key: 'privacy_position', label: 'Position', hint: 'Your job title or role' },
  { key: 'privacy_bio', label: 'Bio', hint: 'Your introduction' },
  { key: 'privacy_skills', label: 'Skills', hint: 'Skills listed on your profile' },
  { key: 'privacy_experience', label: 'Work experience', hint: 'Your work history' },
];

export default function ProfilePage({ onLogout }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ firstname: '', lastname: '', position: '' });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const [bio, setBio] = useState('');
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [savingBio, setSavingBio] = useState(false);

  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState('');
  const [isEditingSkills, setIsEditingSkills] = useState(false);
  const [savingSkills, setSavingSkills] = useState(false);

  const [experience, setExperience] = useState([]);
  const [isEditingExperience, setIsEditingExperience] = useState(false);
  const [savingExperience, setSavingExperience] = useState(false);

  const [privacy, setPrivacy] = useState(defaultPrivacy());
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const API_URL = getApiBaseUrl();

  const normalizeUser = (rawUser) => {
    if (!rawUser) return rawUser;
    return {
      ...rawUser,
      firstname: rawUser.firstname ?? rawUser.first_name ?? '',
      lastname: rawUser.lastname ?? rawUser.last_name ?? '',
      position: rawUser.position ?? '',
      bio: rawUser.bio ?? '',
      skills: Array.isArray(rawUser.skills) ? rawUser.skills : [],
      experience: Array.isArray(rawUser.experience) ? rawUser.experience : [],
    };
  };

  const [newProfilePic, setNewProfilePic] = useState(null);
  const [previewPic, setPreviewPic] = useState('');
  const [isParsingResume, setIsParsingResume] = useState(false);
  const [resumeAutofillHint, setResumeAutofillHint] = useState('');

  const applyUserState = (normalizedUser) => {
    setUser(normalizedUser);
    setFormData({
      firstname: normalizedUser.firstname,
      lastname: normalizedUser.lastname,
      position: normalizedUser.position || '',
    });
    setBio(normalizedUser.bio || '');
    setSkills(
      (normalizedUser.skills || []).map((s) =>
        typeof s === 'string' ? s : s.skill_name
      )
    );
    setExperience(
      (normalizedUser.experience || []).map((e) => ({
        job_title: e.job_title || '',
        company: e.company || '',
        start_date: e.start_date || '',
        end_date: e.end_date || '',
        is_current: !!e.is_current,
        description: e.description || '',
      }))
    );
    const defaults = defaultPrivacy();
    setPrivacy({
      privacy_profile: normalizedUser.privacy_profile || defaults.privacy_profile,
      privacy_email: normalizedUser.privacy_email || defaults.privacy_email,
      privacy_country: normalizedUser.privacy_country || defaults.privacy_country,
      privacy_position: normalizedUser.privacy_position || defaults.privacy_position,
      privacy_bio: normalizedUser.privacy_bio || defaults.privacy_bio,
      privacy_skills: normalizedUser.privacy_skills || defaults.privacy_skills,
      privacy_experience: normalizedUser.privacy_experience || defaults.privacy_experience,
    });
  };

  const refreshUserData = async () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('forumUser'));
      if (!storedUser) return;

      const res = await fetch(`${API_URL}/getusers.php?user_id=${storedUser.id}`);
      const data = await res.json();

      if (data.success && data.user) {
        applyUserState(normalizeUser(data.user));
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('forumUser');
    if (!storedUser) {
      setError('User not logged in.');
      return;
    }

    const parsedUser = JSON.parse(storedUser);
    const userId = parsedUser.id;

    fetch(`${API_URL}/getusers.php?user_id=${userId}`)
      .then(async (res) => {
        const raw = await res.text();
        let data;
        try {
          data = JSON.parse(raw);
        } catch (parseErr) {
          throw new Error('Server returned invalid JSON.');
        }
        if (!res.ok) {
          throw new Error(data?.message || 'Failed to fetch user data');
        }
        return data;
      })
      .then((data) => {
        if (!data.success || !data.user) {
          setError(data.message || 'User not found');
        } else {
          applyUserState(normalizeUser(data.user));
        }
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        setError('Something went wrong while loading your profile.');
      });
  }, [API_URL]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_URL}/update_user_name.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          firstname: formData.firstname,
          lastname: formData.lastname,
          position: formData.position || '',
        }),
      });

      const result = await response.json();
      if (result.success) {
        setUser((prev) => ({
          ...prev,
          firstname: formData.firstname,
          lastname: formData.lastname,
          first_name: formData.firstname,
          last_name: formData.lastname,
          position: formData.position || '',
        }));
        // Keep localStorage in sync so new posts show the updated name/position
        try {
          const stored = JSON.parse(localStorage.getItem('forumUser') || '{}');
          localStorage.setItem(
            'forumUser',
            JSON.stringify({
              ...stored,
              firstname: formData.firstname,
              lastname: formData.lastname,
              first_name: formData.firstname,
              last_name: formData.lastname,
              position: formData.position || '',
            })
          );
        } catch (_) {
          /* ignore */
        }
        alert('Profile updated successfully!');
        setIsEditing(false);
      } else {
        alert(result.message || 'Failed to update name.');
      }
    } catch (err) {
      console.error('Update error:', err);
      alert('An error occurred while saving.');
    }
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewProfilePic(file);
      setPreviewPic(URL.createObjectURL(file));
    }
  };

  const normalizeExperienceEntry = (entry) => {
    const jobTitle = String(entry?.job_title || '').trim();
    const company = String(entry?.company || '').trim();
    if (!jobTitle || !company) return null;
    return {
      job_title: jobTitle,
      company,
      start_date: String(entry?.start_date || '').trim(),
      end_date: String(entry?.end_date || '').trim(),
      is_current: !!entry?.is_current,
      description: String(entry?.description || '').trim(),
    };
  };

  const applyResumeAutofill = (parsedProfile) => {
    if (!parsedProfile || typeof parsedProfile !== 'object') return;

    const first = String(parsedProfile.firstname || '').trim();
    const last = String(parsedProfile.lastname || '').trim();
    const nextPosition = String(parsedProfile.position || '').trim();
    const nextBio = String(parsedProfile.bio || '').trim();
    const nextSkills = Array.isArray(parsedProfile.skills)
      ? parsedProfile.skills
          .map((skill) => String(skill || '').trim())
          .filter(Boolean)
          .slice(0, 50)
      : [];
    const nextExperience = Array.isArray(parsedProfile.experience)
      ? parsedProfile.experience.map(normalizeExperienceEntry).filter(Boolean).slice(0, 30)
      : [];

    if (first || last || nextPosition) {
      setFormData((prev) => ({
        ...prev,
        firstname: first || prev.firstname,
        lastname: last || prev.lastname,
        position: nextPosition || prev.position,
      }));
      setUser((prev) => ({
        ...prev,
        firstname: first || prev.firstname,
        lastname: last || prev.lastname,
        first_name: first || prev.first_name,
        last_name: last || prev.last_name,
        position: nextPosition || prev.position,
      }));
      setIsEditing(true);
    }

    if (nextBio) {
      setBio(nextBio);
      setUser((prev) => ({ ...prev, bio: nextBio }));
    }

    if (nextSkills.length) {
      setSkills(nextSkills);
      setUser((prev) => ({
        ...prev,
        skills: nextSkills.map((skillName, index) => ({
          id: index + 1,
          skill_name: skillName,
          sort_order: index,
        })),
      }));
    }

    if (nextExperience.length) {
      setExperience(nextExperience);
      setUser((prev) => ({ ...prev, experience: nextExperience }));
    }
  };

  const handleResumeUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!user?.id) {
      alert('User not loaded yet. Please try again in a moment.');
      return;
    }

    setIsParsingResume(true);
    setResumeAutofillHint('');
    const payload = new FormData();
    payload.append('user_id', String(user.id));
    payload.append('resume', file);

    try {
      const res = await fetch(`${API_URL}/parse_resume.php`, {
        method: 'POST',
        body: payload,
      });
      const raw = await res.text();
      let result;
      try {
        result = JSON.parse(raw);
      } catch (_) {
        throw new Error('Server returned invalid response.');
      }
      if (!res.ok || !result?.success) {
        throw new Error(result?.message || 'Failed to parse resume.');
      }
      applyResumeAutofill(result.profile || {});
      setResumeAutofillHint(
        'Resume details added. Review the fields, then save each section.'
      );
    } catch (err) {
      console.error('Resume parsing error:', err);
      alert(err.message || 'Unable to process this resume file.');
    } finally {
      setIsParsingResume(false);
    }
  };

  const handleUploadProfilePic = async () => {
    if (!newProfilePic) {
      alert('Please select an image first.');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('user_id', user.id);
    formDataUpload.append('profile_pic', newProfilePic);

    try {
      const res = await fetch(`${API_URL}/update_profile_pic.php`, {
        method: 'POST',
        body: formDataUpload,
      });

      const result = await res.json();
      if (result.success) {
        alert('Profile picture updated!');
        setNewProfilePic(null);
        setPreviewPic('');
        await refreshUserData();
      } else {
        alert(result.message || 'Failed to update picture.');
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Error while uploading picture.');
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handlePasswordSave = async () => {
    if (passwordData.new_password.length < 8) {
      alert('New password must be at least 8 characters.');
      return;
    }
    if (passwordData.new_password !== confirmPassword) {
      alert('New password and confirmation do not match.');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/update_password.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        }),
      });

      const result = await res.json();
      if (result.success) {
        alert('Password updated successfully!');
        setPasswordData({ current_password: '', new_password: '' });
        setConfirmPassword('');
        setIsUpdatingPassword(false);
      } else {
        alert(result.message || 'Failed to update password.');
      }
    } catch (err) {
      console.error('Password update error:', err);
      alert('Error while updating password.');
    }
  };

  const handleSaveBio = async () => {
    setSavingBio(true);
    try {
      const res = await fetch(`${API_URL}/update_user_bio.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, bio }),
      });
      const result = await res.json();
      if (result.success) {
        setUser((prev) => ({ ...prev, bio }));
        setIsEditingBio(false);
        alert('Bio updated successfully!');
      } else {
        alert(result.message || 'Failed to update bio.');
      }
    } catch (err) {
      console.error('Bio update error:', err);
      alert('Error while updating bio.');
    } finally {
      setSavingBio(false);
    }
  };

  const addSkill = () => {
    const name = skillInput.trim();
    if (!name) return;
    if (skills.some((s) => s.toLowerCase() === name.toLowerCase())) {
      setSkillInput('');
      return;
    }
    if (skills.length >= 50) {
      alert('Maximum 50 skills allowed.');
      return;
    }
    setSkills((prev) => [...prev, name]);
    setSkillInput('');
  };

  const removeSkill = (index) => {
    setSkills((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveSkills = async () => {
    setSavingSkills(true);
    try {
      const res = await fetch(`${API_URL}/update_user_skills.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, skills }),
      });
      const result = await res.json();
      if (result.success) {
        const next = (result.skills || []).map((s) => s.skill_name);
        setSkills(next);
        setUser((prev) => ({ ...prev, skills: result.skills || [] }));
        setIsEditingSkills(false);
        alert('Skills updated successfully!');
      } else {
        alert(result.message || 'Failed to update skills.');
      }
    } catch (err) {
      console.error('Skills update error:', err);
      alert('Error while updating skills.');
    } finally {
      setSavingSkills(false);
    }
  };

  const updateExperienceField = (index, field, value) => {
    setExperience((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const next = { ...item, [field]: value };
        if (field === 'is_current' && value) {
          next.end_date = '';
        }
        return next;
      })
    );
  };

  const addExperience = () => {
    if (experience.length >= 30) {
      alert('Maximum 30 work experience entries allowed.');
      return;
    }
    setExperience((prev) => [...prev, emptyExperience()]);
  };

  const removeExperience = (index) => {
    setExperience((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveExperience = async () => {
    for (const item of experience) {
      if (!item.job_title.trim() || !item.company.trim()) {
        alert('Each work experience entry needs a job title and company.');
        return;
      }
    }

    setSavingExperience(true);
    try {
      const res = await fetch(`${API_URL}/update_user_experience.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, experience }),
      });
      const result = await res.json();
      if (result.success) {
        const next = (result.experience || []).map((e) => ({
          job_title: e.job_title || '',
          company: e.company || '',
          start_date: e.start_date || '',
          end_date: e.end_date || '',
          is_current: !!e.is_current,
          description: e.description || '',
        }));
        setExperience(next);
        setUser((prev) => ({ ...prev, experience: result.experience || [] }));
        setIsEditingExperience(false);
        alert('Work experience updated successfully!');
      } else {
        alert(result.message || 'Failed to update work experience.');
      }
    } catch (err) {
      console.error('Experience update error:', err);
      alert('Error while updating work experience.');
    } finally {
      setSavingExperience(false);
    }
  };

  const handlePrivacyChange = (key, value) => {
    setPrivacy((prev) => ({ ...prev, [key]: value }));
  };

  const handleSavePrivacy = async () => {
    setSavingPrivacy(true);
    try {
      const res = await fetch(`${API_URL}/update_privacy.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, ...privacy }),
      });
      const result = await res.json();
      if (result.success) {
        const next = { ...privacy, ...(result.privacy || {}) };
        setPrivacy(next);
        setUser((prev) => ({ ...prev, ...next }));
        alert('Privacy settings saved.');
      } else {
        alert(result.message || 'Failed to save privacy settings.');
      }
    } catch (err) {
      console.error('Privacy update error:', err);
      alert('Error while saving privacy settings.');
    } finally {
      setSavingPrivacy(false);
    }
  };

  const closeDeleteModal = () => {
    if (deletingAccount) return;
    setShowDeleteModal(false);
    setDeletePassword('');
    setDeleteConfirmation('');
    setDeleteError('');
  };

  useEffect(() => {
    if (!showDeleteModal) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeDeleteModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showDeleteModal, deletingAccount]);

  const handleDeleteAccount = async () => {
    if (deleteConfirmation.trim().toUpperCase() !== 'DELETE') {
      setDeleteError('Type DELETE to confirm.');
      return;
    }
    if (!deletePassword) {
      setDeleteError('Enter your password to continue.');
      return;
    }

    setDeletingAccount(true);
    setDeleteError('');
    try {
      const res = await fetch(`${API_URL}/delete_own_account.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          password: deletePassword,
          confirmation: deleteConfirmation,
        }),
      });
      const result = await res.json();
      if (result.success) {
        clearAuthToken();
        localStorage.removeItem('forumUser');
        if (typeof onLogout === 'function') {
          onLogout();
        } else {
          window.location.href = '/';
        }
      } else {
        setDeleteError(result.message || 'Failed to delete account.');
      }
    } catch (err) {
      console.error('Delete account error:', err);
      setDeleteError('Error while deleting account. Please try again.');
    } finally {
      setDeletingAccount(false);
    }
  };

  const formatPeriod = (item) => {
    const start = item.start_date || '';
    const end = item.is_current ? 'Present' : item.end_date || '';
    if (start && end) return `${start} – ${end}`;
    if (start) return start;
    if (end) return end;
    return '';
  };

  if (error) return <div className="mp-error">{error}</div>;
  if (!user) {
    return (
      <div className="mp-loading">
        <Loading />
      </div>
    );
  }

  const imageUrl = previewPic || resolveAvatarUrl(user, API_URL);
  const displayName =
    `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.username;

  return (
    <div className="mp-page">
      <div className="mp-shell">
        <div className="mp-page-title">
          <span className="mp-page-title-icon" aria-hidden="true">
            <User size={20} strokeWidth={2.2} />
          </span>
          <h1>My Profile</h1>
        </div>

        <section className="mp-card mp-hero">
          <div>
            <div className="mp-avatar-wrap">
              <img
                src={imageUrl}
                alt="Profile"
                className="mp-avatar"
                onError={handleAvatarError}
              />
              <label className="mp-avatar-cam" htmlFor="profilePicInput" title="Change Profile Picture">
                <Camera size={15} strokeWidth={2.3} />
                <input
                  type="file"
                  id="profilePicInput"
                  accept="image/*"
                  onChange={handleProfilePicChange}
                />
              </label>
            </div>
            {newProfilePic && (
              <button type="button" className="mp-save-photo" onClick={handleUploadProfilePic}>
                Save Photo
              </button>
            )}
          </div>

          <div className="mp-hero-main">
            <div className="mp-hero-name-row">
              <h2 className="mp-hero-name">{displayName}</h2>
              {user.typeofuser && (
                <span className="mp-role-pill">{user.typeofuser}</span>
              )}
            </div>
            <p className="mp-username">({user.username})</p>
            <div className="mp-joined">
              <Calendar size={14} strokeWidth={2.2} />
              Joined on {new Date(user.created_at).toLocaleDateString()}
            </div>
            <div className="mp-stats">
              <Link to="/network?tab=connections" className="mp-stat">
                <Users size={15} strokeWidth={2.2} />
                {user.connection_count ?? 0} <span>Connections</span>
              </Link>
              <Link to="/network?tab=followers" className="mp-stat">
                <UserPlus size={15} strokeWidth={2.2} />
                {user.follower_count ?? 0} <span>Followers</span>
              </Link>
              <Link to="/network?tab=following" className="mp-stat">
                <UserCircle2 size={15} strokeWidth={2.2} />
                {user.following_count ?? 0} <span>Following</span>
              </Link>
            </div>
          </div>

          <Link to="/network" className="mp-network-btn">
            <Users size={16} strokeWidth={2.3} />
            My Network
          </Link>
        </section>

        <section className="mp-card mp-card-pad">
          <div className="mp-card-head">
            <div className="mp-card-head-left">
              <span className="mp-section-icon blue" aria-hidden="true">
                <IdCard size={17} strokeWidth={2.2} />
              </span>
              <h2>General Information</h2>
            </div>
            <div className="mp-profile-actions">
              <label className="mp-btn mp-btn-outline" htmlFor="mp-resume-upload-input">
                <Upload size={14} strokeWidth={2.3} />
                {isParsingResume ? 'Parsing CV...' : 'Upload CV/Resume'}
                <input
                  id="mp-resume-upload-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleResumeUpload}
                  disabled={isParsingResume}
                />
              </label>
              {!isEditing && (
                <button
                  type="button"
                  className="mp-btn mp-btn-outline"
                  onClick={() => setIsEditing(true)}
                >
                  <Pencil size={14} strokeWidth={2.3} />
                  Edit Name
                </button>
              )}
            </div>
          </div>
          {resumeAutofillHint && <p className="mp-autofill-hint">{resumeAutofillHint}</p>}

          <div className="mp-info-grid">
            <div className="mp-info-item">
              <span className="mp-info-icon" aria-hidden="true">
                <User size={15} />
              </span>
              <div className="mp-info-copy">
                <span className="mp-info-label">Name</span>
                <div className="mp-info-value">
                  {isEditing ? (
                    <div className="mp-edit-row" style={{ marginTop: 0 }}>
                      <input
                        className="mp-input"
                        name="firstname"
                        value={formData.firstname}
                        onChange={handleChange}
                        placeholder="First name"
                      />
                      <input
                        className="mp-input"
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleChange}
                        placeholder="Last name"
                      />
                    </div>
                  ) : (
                    displayName
                  )}
                </div>
              </div>
            </div>

            <div className="mp-info-item">
              <span className="mp-info-icon" aria-hidden="true">
                <Briefcase size={15} />
              </span>
              <div className="mp-info-copy">
                <span className="mp-info-label">Position</span>
                <div className="mp-info-value">
                  {isEditing ? (
                    <input
                      className="mp-input"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      placeholder="e.g. Software Engineer"
                    />
                  ) : (
                    user.position || '—'
                  )}
                </div>
              </div>
            </div>

            <div className="mp-info-item">
              <span className="mp-info-icon" aria-hidden="true">
                <UserCircle2 size={15} />
              </span>
              <div className="mp-info-copy">
                <span className="mp-info-label">Username</span>
                <div className="mp-info-value">{user.username}</div>
              </div>
            </div>

            <div className="mp-info-item">
              <span className="mp-info-icon" aria-hidden="true">
                <Mail size={15} />
              </span>
              <div className="mp-info-copy">
                <span className="mp-info-label">Email</span>
                <div className="mp-info-value">{user.email}</div>
              </div>
            </div>

            <div className="mp-info-item">
              <span className="mp-info-icon" aria-hidden="true">
                <MapPin size={15} />
              </span>
              <div className="mp-info-copy">
                <span className="mp-info-label">Country</span>
                <div className="mp-info-value">{user.country || '—'}</div>
              </div>
            </div>

            <div className="mp-info-item">
              <span className="mp-info-icon" aria-hidden="true">
                <Shield size={15} />
              </span>
              <div className="mp-info-copy">
                <span className="mp-info-label">Profile</span>
                <div className="mp-info-value">{user.typeofuser || '—'}</div>
              </div>
            </div>
          </div>

          {isEditing && (
            <div className="mp-edit-row">
              <button type="button" className="mp-btn mp-btn-primary" onClick={handleSave}>
                Save
              </button>
              <button
                type="button"
                className="mp-btn mp-btn-muted"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </button>
            </div>
          )}
        </section>

        <section className="mp-card mp-action-card bio">
          <div className="mp-action-main">
            <h3 className="mp-action-title">
              <span className="mp-section-icon green" aria-hidden="true">
                <Pencil size={16} />
              </span>
              Bio
            </h3>
            {isEditingBio ? (
              <>
                <textarea
                  className="mp-textarea"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder="Tell others about yourself..."
                />
                <div className="mp-hint">{bio.length}/2000</div>
                <div className="mp-edit-row">
                  <button
                    type="button"
                    className="mp-btn mp-btn-green"
                    onClick={handleSaveBio}
                    disabled={savingBio}
                  >
                    {savingBio ? 'Saving...' : 'Save Bio'}
                  </button>
                  <button
                    type="button"
                    className="mp-btn mp-btn-muted"
                    onClick={() => {
                      setBio(user.bio || '');
                      setIsEditingBio(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <p className="mp-action-body">
                {bio || 'No bio yet. Add a short introduction about yourself'}
              </p>
            )}
          </div>
          {!isEditingBio && (
            <div className="mp-action-side">
              <button
                type="button"
                className="mp-btn mp-btn-green"
                onClick={() => setIsEditingBio(true)}
              >
                {bio ? <Pencil size={14} /> : <Plus size={14} />}
                {bio ? 'Edit Bio' : 'Add Bio'}
              </button>
            </div>
          )}
        </section>

        <section className="mp-card mp-action-card skills">
          <div className="mp-action-main">
            <h3 className="mp-action-title">
              <span className="mp-section-icon purple" aria-hidden="true">
                <Sparkles size={16} />
              </span>
              Skills
            </h3>
            {isEditingSkills ? (
              <>
                <div className="mp-skill-list">
                  {skills.map((skill, index) => (
                    <span key={`${skill}-${index}`} className="mp-skill-chip">
                      {skill}
                      <button
                        type="button"
                        onClick={() => removeSkill(index)}
                        aria-label={`Remove ${skill}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="mp-edit-row">
                  <input
                    className="mp-input"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSkill();
                      }
                    }}
                    placeholder="e.g. Programming, Graphic Design"
                  />
                  <button type="button" className="mp-btn mp-btn-purple" onClick={addSkill}>
                    Add Skill
                  </button>
                </div>
                <div className="mp-edit-row">
                  <button
                    type="button"
                    className="mp-btn mp-btn-purple"
                    onClick={handleSaveSkills}
                    disabled={savingSkills}
                  >
                    {savingSkills ? 'Saving...' : 'Save Skills'}
                  </button>
                  <button
                    type="button"
                    className="mp-btn mp-btn-muted"
                    onClick={() => {
                      setSkills(
                        (user.skills || []).map((s) =>
                          typeof s === 'string' ? s : s.skill_name
                        )
                      );
                      setSkillInput('');
                      setIsEditingSkills(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : skills.length === 0 ? (
              <p className="mp-action-body">
                No skills listed yet. Add professional skills such as Programming,
                Project Management, or Digital Marketing
              </p>
            ) : (
              <div className="mp-skill-list">
                {skills.map((skill, index) => (
                  <span key={`${skill}-${index}`} className="mp-skill-chip">
                    {skill}
                  </span>
                ))}
              </div>
            )}
          </div>
          {!isEditingSkills && (
            <div className="mp-action-side">
              <button
                type="button"
                className="mp-btn mp-btn-purple"
                onClick={() => setIsEditingSkills(true)}
              >
                {skills.length ? <Pencil size={14} /> : <Plus size={14} />}
                {skills.length ? 'Edit Skills' : 'Add Skills'}
              </button>
            </div>
          )}
        </section>

        <section className="mp-card mp-action-card experience">
          <div className="mp-action-main">
            <h3 className="mp-action-title">
              <span className="mp-section-icon orange" aria-hidden="true">
                <Briefcase size={16} />
              </span>
              Work Experience
            </h3>
            {isEditingExperience ? (
              <>
                {experience.map((item, index) => (
                  <div key={index} className="mp-exp-editor">
                    <div className="mp-exp-editor-head">
                      <span>Experience #{index + 1}</span>
                      <button
                        type="button"
                        className="mp-btn mp-btn-muted"
                        onClick={() => removeExperience(index)}
                      >
                        Remove
                      </button>
                    </div>
                    <label className="mp-field-label">Job Title</label>
                    <input
                      className="mp-input"
                      style={{ maxWidth: '100%' }}
                      value={item.job_title}
                      onChange={(e) =>
                        updateExperienceField(index, 'job_title', e.target.value)
                      }
                      placeholder="e.g. Software Engineer"
                    />
                    <label className="mp-field-label">Company / Organization</label>
                    <input
                      className="mp-input"
                      style={{ maxWidth: '100%' }}
                      value={item.company}
                      onChange={(e) =>
                        updateExperienceField(index, 'company', e.target.value)
                      }
                      placeholder="e.g. Acme Corp"
                    />
                    <div className="mp-edit-row">
                      <div style={{ flex: '1 1 160px' }}>
                        <label className="mp-field-label">Start</label>
                        <input
                          className="mp-input"
                          style={{ maxWidth: '100%' }}
                          value={item.start_date}
                          onChange={(e) =>
                            updateExperienceField(index, 'start_date', e.target.value)
                          }
                          placeholder="e.g. Jan 2020"
                        />
                      </div>
                      <div style={{ flex: '1 1 160px' }}>
                        <label className="mp-field-label">End</label>
                        <input
                          className="mp-input"
                          style={{
                            maxWidth: '100%',
                            background: item.is_current ? '#eee' : '#fff',
                          }}
                          value={item.end_date}
                          onChange={(e) =>
                            updateExperienceField(index, 'end_date', e.target.value)
                          }
                          placeholder="e.g. Dec 2022"
                          disabled={item.is_current}
                        />
                      </div>
                    </div>
                    <label className="mp-check">
                      <input
                        type="checkbox"
                        checked={!!item.is_current}
                        onChange={(e) =>
                          updateExperienceField(index, 'is_current', e.target.checked)
                        }
                      />
                      I currently work here
                    </label>
                    <label className="mp-field-label">
                      Responsibilities & Achievements
                    </label>
                    <textarea
                      className="mp-textarea"
                      value={item.description}
                      onChange={(e) =>
                        updateExperienceField(index, 'description', e.target.value)
                      }
                      rows={3}
                      placeholder="Describe your role and key achievements..."
                    />
                  </div>
                ))}
                <div className="mp-edit-row">
                  <button
                    type="button"
                    className="mp-btn mp-btn-orange"
                    onClick={addExperience}
                  >
                    <Plus size={14} />
                    Add Experience
                  </button>
                </div>
                <div className="mp-edit-row">
                  <button
                    type="button"
                    className="mp-btn mp-btn-orange"
                    onClick={handleSaveExperience}
                    disabled={savingExperience}
                  >
                    {savingExperience ? 'Saving...' : 'Save Experience'}
                  </button>
                  <button
                    type="button"
                    className="mp-btn mp-btn-muted"
                    onClick={() => {
                      setExperience(
                        (user.experience || []).map((e) => ({
                          job_title: e.job_title || '',
                          company: e.company || '',
                          start_date: e.start_date || '',
                          end_date: e.end_date || '',
                          is_current: !!e.is_current,
                          description: e.description || '',
                        }))
                      );
                      setIsEditingExperience(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : experience.length === 0 ? (
              <p className="mp-action-body">
                No work experience listed yet. Add your previous and current roles
              </p>
            ) : (
              experience.map((item, index) => (
                <div key={index} className="mp-exp-item">
                  <div className="mp-exp-title">{item.job_title}</div>
                  <div className="mp-exp-company">{item.company}</div>
                  {formatPeriod(item) && (
                    <div className="mp-exp-period">{formatPeriod(item)}</div>
                  )}
                  {item.description && (
                    <p className="mp-exp-desc">{item.description}</p>
                  )}
                </div>
              ))
            )}
          </div>
          {!isEditingExperience && (
            <div className="mp-action-side">
              <button
                type="button"
                className="mp-btn mp-btn-orange"
                onClick={() => setIsEditingExperience(true)}
              >
                {experience.length ? <Pencil size={14} /> : <Plus size={14} />}
                {experience.length ? 'Edit Work Experience' : 'Add Work Experience'}
              </button>
            </div>
          )}
        </section>

        <section className="mp-card mp-card-pad">
          <div className="mp-card-head">
            <div className="mp-card-head-left">
              <span className="mp-section-icon blue" aria-hidden="true">
                <Eye size={17} strokeWidth={2.2} />
              </span>
              <h2>Privacy &amp; Security</h2>
            </div>
          </div>
          <p className="mp-privacy-intro">
            Choose who can view your profile and which details other learners can see.
          </p>

          <h3 className="mp-privacy-sub">Who can see your profile</h3>
          <div className="mp-privacy-options" role="radiogroup" aria-label="Profile visibility">
            {PROFILE_VISIBILITY.map((option) => {
              const Icon = option.icon;
              const selected = privacy.privacy_profile === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`mp-privacy-option${selected ? ' selected' : ''}`}
                  onClick={() => handlePrivacyChange('privacy_profile', option.value)}
                >
                  <span className="mp-privacy-option-icon" aria-hidden="true">
                    <Icon size={18} strokeWidth={2.2} />
                  </span>
                  <span className="mp-privacy-option-copy">
                    <strong>{option.label}</strong>
                    <span>{option.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <h3 className="mp-privacy-sub">Who can see your information</h3>
          <div className="mp-privacy-rows">
            {INFO_PRIVACY_FIELDS.map((field) => (
              <div key={field.key} className="mp-privacy-row">
                <div className="mp-privacy-row-copy">
                  <span className="mp-privacy-row-label">{field.label}</span>
                  <span className="mp-privacy-row-hint">{field.hint}</span>
                </div>
                <select
                  className="mp-select"
                  value={privacy[field.key]}
                  onChange={(e) => handlePrivacyChange(field.key, e.target.value)}
                  aria-label={`Who can see ${field.label}`}
                >
                  {PRIVACY_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="mp-edit-row">
            <button
              type="button"
              className="mp-btn mp-btn-primary"
              onClick={handleSavePrivacy}
              disabled={savingPrivacy}
            >
              {savingPrivacy ? 'Saving...' : 'Save Privacy Settings'}
            </button>
          </div>
        </section>

        <section className="mp-card mp-password-card">
          {isUpdatingPassword ? (
            <div className="mp-password-form">
              <div className="mp-card-head" style={{ marginBottom: 12 }}>
                <div className="mp-card-head-left">
                  <span className="mp-section-icon blue" aria-hidden="true">
                    <Shield size={17} />
                  </span>
                  <h3>Update Password</h3>
                </div>
              </div>
              <div className="mp-password-fields">
                <input
                  className="mp-input"
                  type="password"
                  name="current_password"
                  placeholder="Current Password"
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                />
                <input
                  className="mp-input"
                  type="password"
                  name="new_password"
                  placeholder="New Password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                />
                <input
                  className="mp-input"
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <div className="mp-edit-row">
                <button
                  type="button"
                  className="mp-btn mp-btn-primary"
                  onClick={handlePasswordSave}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="mp-btn mp-btn-muted"
                  onClick={() => setIsUpdatingPassword(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="mp-card-head-left">
                <span className="mp-section-icon blue" aria-hidden="true">
                  <Shield size={17} />
                </span>
                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800 }}>
                  Update Password
                </h3>
              </div>
              <button
                type="button"
                className="mp-btn mp-btn-outline"
                onClick={() => setIsUpdatingPassword(true)}
              >
                <Lock size={14} />
                Change Password
              </button>
            </>
          )}
        </section>

        <section className="mp-card mp-card-pad mp-danger-card">
          <div className="mp-card-head">
            <div className="mp-card-head-left">
              <span className="mp-section-icon red" aria-hidden="true">
                <Trash2 size={17} strokeWidth={2.2} />
              </span>
              <h2>Delete Account</h2>
            </div>
          </div>
          <p className="mp-action-body">
            Permanently remove your account, profile information, and related data.
            This cannot be undone.
          </p>
          <div className="mp-edit-row">
            <button
              type="button"
              className="mp-btn mp-btn-danger"
              onClick={() => setShowDeleteModal(true)}
            >
              <Trash2 size={14} />
              Delete my account
            </button>
          </div>
        </section>
      </div>

      {showDeleteModal && (
        <div className="mp-modal-backdrop" role="presentation" onClick={closeDeleteModal}>
          <div
            className="mp-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mp-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="mp-modal-close"
              onClick={closeDeleteModal}
              aria-label="Close"
              disabled={deletingAccount}
            >
              <X size={18} />
            </button>
            <div className="mp-modal-icon" aria-hidden="true">
              <AlertTriangle size={22} strokeWidth={2.2} />
            </div>
            <h3 id="mp-delete-title">Delete your account?</h3>
            <p>
              This will permanently delete your profile, posts, connections, and
              saved information. Type <strong>DELETE</strong> and enter your
              password to confirm.
            </p>
            <label className="mp-field-label" htmlFor="mp-delete-confirm">
              Type DELETE to confirm
            </label>
            <input
              id="mp-delete-confirm"
              className="mp-input"
              style={{ maxWidth: '100%' }}
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
              disabled={deletingAccount}
            />
            <label className="mp-field-label" htmlFor="mp-delete-password">
              Password
            </label>
            <input
              id="mp-delete-password"
              className="mp-input"
              style={{ maxWidth: '100%' }}
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Your current password"
              disabled={deletingAccount}
            />
            {deleteError && <p className="mp-modal-error">{deleteError}</p>}
            <div className="mp-edit-row" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="mp-btn mp-btn-muted"
                onClick={closeDeleteModal}
                disabled={deletingAccount}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mp-btn mp-btn-danger"
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
              >
                {deletingAccount ? 'Deleting...' : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
